import styled from '@emotion/styled';
import { Col, Row, Slider as AntSlider } from 'antd';
import React from 'react';

import { color, font, mediaQueries, shadow } from 'src/shared/styles/variables';

import { ProgramWeekEnum } from '../../models/programs';

const RateSliderWrapper = styled(Row)({
    justifyContent: 'space-between',
    boxShadow: shadow.lightCardShadow,
    border: `1px solid ${color.concrete}`,
    borderRadius: '0.25rem',
    padding: '2rem 2rem 1.75rem',
    ':not(:last-child)': {
        marginBottom: '2rem',
    },
});

const Title = styled('h4')(
    {
        color: color.chathamsBlue,
        textTransform: 'capitalize',
        [mediaQueries.md]: {
            marginBottom: '0.25rem',
        },
    },
    font.heavy(font.size.medium),
);

const Definition = styled('p')(
    {
        color: color.tundora,
    },
    font.light(font.size.base),
);

const SliderWrapper = styled(Col)({
    paddingTop: '1rem',
    [mediaQueries.md]: {
        marginTop: '1.5rem',
    },
});

const SliderLabel = styled('span')(
    {
        display: 'block',
        position: 'absolute',
        top: 0,
        color: color.baliHai,
        '&:first-child': {
            left: '0.5rem',
        },
        '&:last-child': {
            right: '0.5rem',
        },
    },
    font.book(font.size.base),
);

const RateSlider = styled(AntSlider)({
    '.ant-slider-rail': {
        height: '0.75rem',
        borderRadius: '0.5rem',
        backgroundColor: color.gallery,
    },
    '.ant-slider-track': {
        height: '0.75rem',
        borderRadius: '0.5rem',
        backgroundImage: `linear-gradient(90deg, ${color.robinsEggBlue}, ${color.blueLagoon})`,
        transition: 'all 0.3s ease-in-out',
    },
    '.ant-slider-step': {
        height: '0.75rem',
    },
    '.ant-slider-dot': {
        display: 'none',
    },
    '.ant-slider-handle': {
        backgroundColor: color.blueLagoon,
        width: '24px',
        height: '24px',
        border: `solid 6px ${color.white}`,
        marginLeft: '-12px',
        marginTop: '-7px',
        boxShadow: shadow.mediumShadow,
        transition: 'all 0.3s ease-in-out',
    },
    ':hover .ant-slider-handle:not(.ant-tooltip-open)': {
        borderColor: color.white,
    },
    '.ant-slider-mark': {
        top: 0,
    },
    '.ant-slider-mark-text': {
        color: color.baliHai,
        paddingTop: '2rem',
        ...font.heavy(font.size.base),
        transition: 'all 0.3s ease-in-out',
        '&.ant-slider-mark-text-active': {
            color: color.bayOfMany,
        },
    },
});

interface Props {
    pillarId: ProgramWeekEnum;
    title: string;
    definition?: string;
    minValue: number;
    maxValue: number;
    value: number;
    minLabelText: string;
    maxLabelText: string;
    onChangeValue: (weekId: ProgramWeekEnum, rate: number) => void;
}

const rateMarks = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
};

export class CorePillarRateSlider extends React.Component<Props> {
    private handleValueChange = (value: number) =>
        this.props.onChangeValue(this.props.pillarId, value);

    public render(): JSX.Element {
        const {
            title,
            definition = '',
            value,
            minValue,
            maxValue,
            minLabelText,
            maxLabelText,
        } = this.props;

        return (
            <RateSliderWrapper>
                <Col md={11}>
                    <Title>{title}</Title>
                    <Definition>{definition}</Definition>
                </Col>
                <SliderWrapper md={{ span: 12, offset: 1 }}>
                    <SliderLabel>{minLabelText}</SliderLabel>
                    <RateSlider
                        min={minValue}
                        max={maxValue}
                        onChange={this.handleValueChange}
                        value={value}
                        step={1}
                        marks={rateMarks}
                    />
                    <SliderLabel>{maxLabelText}</SliderLabel>
                </SliderWrapper>
            </RateSliderWrapper>
        );
    }
}
